import { faRemove } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { DataResult } from "@progress/kendo-data-query";
import axios from "axios";
import DeleteIcon from "@mui/icons-material/Delete";
import { removeFromDataResult } from "../RemoveFromDataResult";
import { confirm } from "../../Layout/Dialog/ConfirmDialog";
import StyledPageMenuIcon from "../../Layout/PageMenu/PageMenuIcon";
import PageMenuItem from "../../Layout/PageMenu/PageMenuItem";
import PageHeaderIconButton from "../../Layout/Header/PageHeaderIconButton";

type Props = {
  readonly url: string;
  readonly selectedIds: Array<number>;
  readonly dataResult: DataResult;
  readonly setDataResult: (dataResult: DataResult) => void;
  readonly afterDeleteItems: () => void;
  readonly renderStyle: "pageHeaderButton" | "pageMenuItem";
  disabled: boolean;
};

export default function DeleteItemsButton({
  url,
  selectedIds,
  dataResult,
  setDataResult,
  afterDeleteItems,
  renderStyle,
  disabled,
}: Props) {
  const deleteItemsRequest = async () => {
    if (selectedIds.length === 0) {
      throw new Error("selectedIdsList is empty");
    }

    await axios.delete(url, {
      data: {
        ids: selectedIds,
      },
    });

    removeFromDataResult(selectedIds, dataResult, setDataResult);

    afterDeleteItems();
  };

  const deleteItemsWithToast = () =>
    toast.promise(deleteItemsRequest, {
      pending: "Loading",
      success: "Items have been deleted",
      error: "Error while deleting items",
    });

  const deleteItems = async () => {
    const message = "Do you really want to delete selected items";
    const isConfirmed = await confirm(message);
    if (!isConfirmed) {
      return;
    }

    await deleteItemsWithToast();
  };

  const noIdsSelected = selectedIds.length === 0;

  return renderStyle === "pageHeaderButton" ? (
    <PageHeaderIconButton
      disabled={disabled || noIdsSelected}
      onClick={deleteItems}
      color="secondary"
    >
      <DeleteIcon />
    </PageHeaderIconButton>
  ) : (
    <PageMenuItem
      align="right"
      type="link"
      disabled={disabled || noIdsSelected}
      onClick={deleteItems}
    >
      <StyledPageMenuIcon icon={faRemove} />
      Delete
    </PageMenuItem>
  );
}
